import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react"
/**
 * HeroWithGlassCards (React + Tailwind)
 * Style: background image + dark overlay + glass cards on right
 * Colors: #BEE362 (accent), #194464 (primary)
 *
 * Usage:
 * <HeroWithGlassCards bgImageUrl="/images/your-bg.jpg" />
 */

const ACCENT = "#BEE362";
const PRIMARY = "#194464";

/* ---------------------- Small inline icons ---------------------- */
const IconTarget = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M12 21a9 9 0 100-18 9 9 0 000 18z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M12 16a4 4 0 100-8 4 4 0 000 8z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M12 12h.01"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const IconMegaphone = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M10 10l9-4v12l-9-4H6a3 3 0 010-6h4z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 14.5l1 4a2 2 0 002.5 1.4l1-.3-1.2-5.1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const IconUsers = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M17 10a3 3 0 100-6 3 3 0 000 6z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M3.5 20c.8-3.8 3.7-6 7.5-6s6.7 2.2 7.5 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14.5 14.5c2.6.4 4.4 2 5 5.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconWeb = (props) => (
  <svg viewBox="0 0 24 24" fill="none" {...props}>
    <rect
      x="3"
      y="5"
      width="18"
      height="14"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M7 13h6M7 16h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M17.5 20l-1.2-3 3 1.2-1.8 1.8z"
      fill="currentColor"
      opacity=".9"
    />
  </svg>
);

/* ---------------------- Glass Card ---------------------- */
function GlassCard({ title, Icon, accentText }) {
  return (
    <div className="group relative rounded-2xl p-[1px]">
      {/* border gradient */}
      <div
        className="absolute inset-0 rounded-2xl opacity-80"
        style={{
          background:
            "linear-gradient(135deg, rgba(190,227,98,0.55), rgba(255,255,255,0.10), rgba(25,68,100,0.35))",
        }}
      />
      {/* glass */}
      <div className="relative rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-5">
        <div className="flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-xl grid place-items-center"
            style={{
              background: `linear-gradient(135deg, rgba(190,227,98,0.35), rgba(255,255,255,0.08))`,
              color: ACCENT,
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            <Icon className="h-6 w-6" />
          </div>

          <div className="flex-1">
            <div className="text-white font-semibold leading-snug text-[15px] md:text-[16px]">
              {title}
            </div>

            {accentText ? (
              <div className="mt-2 text-sm">
                <span
                  className="font-semibold"
                  style={{ color: ACCENT }}
                >
                  {accentText}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- Main Section ---------------------- */
export default function WhoItsForCards({
  bgImageUrl = "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=2000&q=80",
}) {
  const cards = [
    {
      title: "Unternehmen, die mehr Anfragen & Umsatz erzielen wollen",
      Icon: IconTarget,
    },
    {
      title: "Marken, die sich klar vom Wettbewerb abheben möchten",
      Icon: IconMegaphone,
    },
    {
      title: "Dienstleister, Start-ups & KMUs mit Qualitätsanspruch",
      Icon: IconUsers,
    },
    {
      title: "Alle, die keine Baukasten-Webseiten wollen",
      Icon: IconWeb,
    },
  ];

  return (
    <section className="w-[90%] 2xl:w-[70%] mx-auto mt-16">
      <div className="relative overflow-hidden rounded-3xl">
        {/* Background */}
        <div
          className="absolute inset-0 bg-center bg-cover no-repeat"
        //   style={{ backgroundImage: `url(${bgImageUrl})` }}
        />

        {/* Dark overlay + slight brand tint */}
        <div
          className="absolute inset-0 bg-[#07131d]"
         
        />

        {/* Content */}
        <div className="relative z-10 px-6 py-10 md:px-12 md:py-14">
          <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* LEFT */}
          {/* LEFT */}
<div className="lg:col-span-7">
  <div
    className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-white/20 bg-white/10 backdrop-blur"
    style={{ color: ACCENT }}
  >
    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ACCENT }} />
    Für wen wir Websites entwickeln
  </div>

  <h2 className="mt-5 !text-white h2-mb h2-md h2-lg h2-2xl">
    Maßgeschneiderte Websites für{" "}
    <span style={{ color: ACCENT }}>Wachstum</span> & klare Positionierung.
  </h2>

  <p className="mt-4 text-white/80 max-w-xl">
    Nicht jede Website passt zu jedem Business — deshalb arbeiten wir gezielt mit
    Unternehmen, die Wert auf Qualität, Differenzierung und echte Ergebnisse legen.
  </p>

  {/* Small “hint” line that matches the right cards */}
  <div className="mt-5 flex flex-wrap items-center gap-3 text-white/70 text-sm">
   
  </div>

  <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/kontak"
                  className="group inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black shadow transition hover:bg-white/90"
                >
                  Kontakt
                  <ArrowRight
                    size={16}
                    className="transition group-hover:translate-x-0.5"
                  />
                </Link>

                <Link
                  to="/unverbindliches-angebot"
                  className="inline-flex items-center rounded-xl border border-white/55 bg-[#0b2b40] px-5 py-2.5 text-sm font-semibold text-white/85 transition hover:bg-[#0b2b40]/90"
                >
                  Angebot einholen
                </Link>
              </div>

  <div className="mt-6 text-white/70 text-sm">
    ✓ Individuelles Design &nbsp;&nbsp; ✓ Performance & SEO &nbsp;&nbsp; ✓ Fokus auf Anfragen/Leads
  </div>
</div>



            {/* RIGHT */}
            <div className="lg:col-span-5">
              <div className="space-y-4">
                {cards.map((c, i) => (
                  <GlassCard key={i} title={c.title} Icon={c.Icon} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* subtle bottom highlight */}
        <div
          className="absolute inset-x-0 bottom-0 h-24 opacity-70"
          style={{
            background:
              "linear-gradient(to top, rgba(190,227,98,0.18), transparent)",
          }}
        />
      </div>
    </section>
  );
}
